/**
 * categoryGroupsSlice.js
 *   fetchCategoryGroups       GET  /api/category_groups/
 *   fetchCategoryGroupSummary GET  /api/category_groups/summary/
 *   fetchCategoryGroupById    GET  /api/category_groups/<id>/
 *   createCategoryGroup       POST /api/category_groups/
 *   updateCategoryGroup       PATCH /api/category_groups/<id>/
 *   deleteCategoryGroup       DELETE /api/category_groups/<id>/
 *   searchCategoryGroups      POST /api/category_groups/search/
 *   importCategoryGroupsCSV   POST /api/category_groups/import_csv/
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { logout } from "../actions/authenActions";

const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000";
const BASE = `${baseURL}/emis_pathology/api/category_groups`;

//  Auth header helper
const authHeader = (token) => ({ Authorization: `Bearer ${token}` });

//  Handle 403 + rejectWithValue
const handleError = (error, dispatch, rejectWithValue) => {
  if (error.response?.status === 403) dispatch(logout());
  return rejectWithValue(
    error.response?.data || error.message || "Something went wrong",
  );
};

//  Thunks

/** Fetch all category groups. Optionally pass search string. */
export const fetchCategoryGroups = createAsyncThunk(
  "categoryGroups/fetchAll",
  async ({ access_token, search = "" } = {}, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.get(BASE + "/", {
        headers: authHeader(access_token),
        params: search ? { search } : {},
      });
      return res.data;
    } catch (err) {
      return handleError(err, dispatch, rejectWithValue);
    }
  },
);

/** Fetch lightweight summary list (id + group_name) for dropdowns. */
export const fetchCategoryGroupSummary = createAsyncThunk(
  "categoryGroups/fetchSummary",
  async ({ access_token }, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE}/summary/`, {
        headers: authHeader(access_token),
      });
      return res.data;
    } catch (err) {
      return handleError(err, dispatch, rejectWithValue);
    }
  },
);

/** Fetch a single category group by id. */
export const fetchCategoryGroupById = createAsyncThunk(
  "categoryGroups/fetchById",
  async ({ id, access_token }, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE}/${id}/`, {
        headers: authHeader(access_token),
      });
      return res.data;
    } catch (err) {
      return handleError(err, dispatch, rejectWithValue);
    }
  },
);

/** Create a new category group. */
export const createCategoryGroup = createAsyncThunk(
  "categoryGroups/create",
  async ({ groupData, access_token }, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.post(BASE + "/", groupData, {
        headers: authHeader(access_token),
      });
      return res.data;
    } catch (err) {
      return handleError(err, dispatch, rejectWithValue);
    }
  },
);

/** Partial-update a category group. */
export const updateCategoryGroup = createAsyncThunk(
  "categoryGroups/update",
  async ({ id, groupData, access_token }, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.patch(`${BASE}/${id}/`, groupData, {
        headers: authHeader(access_token),
      });
      return res.data;
    } catch (err) {
      return handleError(err, dispatch, rejectWithValue);
    }
  },
);

/** Delete a category group. */
export const deleteCategoryGroup = createAsyncThunk(
  "categoryGroups/delete",
  async ({ id, access_token }, { dispatch, rejectWithValue }) => {
    try {
      await axios.delete(`${BASE}/${id}/`, {
        headers: authHeader(access_token),
      });
      return id;
    } catch (err) {
      return handleError(err, dispatch, rejectWithValue);
    }
  },
);

/** Full-text search across group_name and category_names. */
export const searchCategoryGroups = createAsyncThunk(
  "categoryGroups/search",
  async ({ query, access_token }, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${BASE}/search/`,
        { query },
        { headers: authHeader(access_token) },
      );
      return res.data;
    } catch (err) {
      return handleError(err, dispatch, rejectWithValue);
    }
  },
);

// /** Upload a CSV file to bulk-import category groups. */
// export const importCategoryGroupsCSV = createAsyncThunk(
//   "categoryGroups/importCSV",
//   async ({ file, dropExisting = false, access_token }, { dispatch, rejectWithValue }) => {
//     try {
//       const formData = new FormData();
//       formData.append("file", file);
//       formData.append("drop_existing", dropExisting ? "true" : "false");
//       const res = await axios.post(`${BASE}/import_csv/`, formData, {
//         headers: {
//           ...authHeader(access_token),
//           "Content-Type": "multipart/form-data",
//         },
//       });
//       return res.data;
//     } catch (err) {
//       return handleError(err, dispatch, rejectWithValue);
//     }
//   }
// );

//  Initial State

const initialState = {
  // Full list
  groups: [],
  // Lightweight summary for selects
  summary: [],
  // Currently-selected / editing group
  selectedGroup: null,
  // Search results (null = not searched yet)
  searchResults: null,
  // CSV import result
  //importResult: null,

  // Loading flags per operation
  loading: {
    fetch: false,
    summary: false,
    detail: false,
    create: false,
    update: false,
    delete: false,
    search: false,
    import: false,
  },
  error: null,
};

//  Slice

const categoryGroupsSlice = createSlice({
  name: "categoryGroups",
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    clearSelectedGroup(state) {
      state.selectedGroup = null;
    },
    clearSearchResults(state) {
      state.searchResults = null;
    },
    // clearImportResult(state) {
    //   state.importResult = null;
    // },
    setSelectedGroup(state, action) {
      state.selectedGroup = action.payload;
    },
  },
  extraReducers: (builder) => {
    //  fetchCategoryGroups
    builder
      .addCase(fetchCategoryGroups.pending, (state) => {
        state.loading.fetch = true;
        state.error = null;
      })
      .addCase(fetchCategoryGroups.fulfilled, (state, action) => {
        state.loading.fetch = false;
        state.groups = action.payload;
      })
      .addCase(fetchCategoryGroups.rejected, (state, action) => {
        state.loading.fetch = false;
        state.error = action.payload;
      });

    //  fetchCategoryGroupSummary
    builder
      .addCase(fetchCategoryGroupSummary.pending, (state) => {
        state.loading.summary = true;
      })
      .addCase(fetchCategoryGroupSummary.fulfilled, (state, action) => {
        state.loading.summary = false;
        state.summary = action.payload;
      })
      .addCase(fetchCategoryGroupSummary.rejected, (state, action) => {
        state.loading.summary = false;
        state.error = action.payload;
      });

    //  fetchCategoryGroupById
    builder
      .addCase(fetchCategoryGroupById.pending, (state) => {
        state.loading.detail = true;
        state.error = null;
      })
      .addCase(fetchCategoryGroupById.fulfilled, (state, action) => {
        state.loading.detail = false;
        state.selectedGroup = action.payload;
      })
      .addCase(fetchCategoryGroupById.rejected, (state, action) => {
        state.loading.detail = false;
        state.error = action.payload;
      });

    //  createCategoryGroup
    builder
      .addCase(createCategoryGroup.pending, (state) => {
        state.loading.create = true;
        state.error = null;
      })
      .addCase(createCategoryGroup.fulfilled, (state, action) => {
        state.loading.create = false;
        state.groups.push(action.payload);
        state.groups.sort((a, b) => a.group_name.localeCompare(b.group_name));
      })
      .addCase(createCategoryGroup.rejected, (state, action) => {
        state.loading.create = false;
        state.error = action.payload;
      });

    //  updateCategoryGroup
    builder
      .addCase(updateCategoryGroup.pending, (state) => {
        state.loading.update = true;
        state.error = null;
      })
      .addCase(updateCategoryGroup.fulfilled, (state, action) => {
        state.loading.update = false;
        const idx = state.groups.findIndex((g) => g.id === action.payload.id);
        if (idx !== -1) state.groups[idx] = action.payload;
        if (state.selectedGroup?.id === action.payload.id) {
          state.selectedGroup = action.payload;
        }
      })
      .addCase(updateCategoryGroup.rejected, (state, action) => {
        state.loading.update = false;
        state.error = action.payload;
      });

    //  deleteCategoryGroup
    builder
      .addCase(deleteCategoryGroup.pending, (state) => {
        state.loading.delete = true;
        state.error = null;
      })
      .addCase(deleteCategoryGroup.fulfilled, (state, action) => {
        state.loading.delete = false;
        state.groups = state.groups.filter((g) => g.id !== action.payload);
        if (state.selectedGroup?.id === action.payload)
          state.selectedGroup = null;
      })
      .addCase(deleteCategoryGroup.rejected, (state, action) => {
        state.loading.delete = false;
        state.error = action.payload;
      });

    //  searchCategoryGroups
    builder
      .addCase(searchCategoryGroups.pending, (state) => {
        state.loading.search = true;
        state.error = null;
      })
      .addCase(searchCategoryGroups.fulfilled, (state, action) => {
        state.loading.search = false;
        state.searchResults = action.payload;
      })
      .addCase(searchCategoryGroups.rejected, (state, action) => {
        state.loading.search = false;
        state.error = action.payload;
      });

    //  importCategoryGroupsCSV
    // builder
    //   .addCase(importCategoryGroupsCSV.pending, (state) => {
    //     state.loading.import = true;
    //     state.importResult = null;
    //     state.error = null;
    //   })
    //   .addCase(importCategoryGroupsCSV.fulfilled, (state, action) => {
    //     state.loading.import = false;
    //     state.importResult = action.payload;
    //   })
    //   .addCase(importCategoryGroupsCSV.rejected, (state, action) => {
    //     state.loading.import = false;
    //     state.error = action.payload;
    //   });
  },
});

export const {
  clearError,
  clearSelectedGroup,
  clearSearchResults,
  clearImportResult,
  setSelectedGroup,
} = categoryGroupsSlice.actions;

//  Selectors
export const selectAllGroups = (state) => state.categoryGroups.groups;
export const selectGroupSummary = (state) => state.categoryGroups.summary;
export const selectSelectedGroup = (state) =>
  state.categoryGroups.selectedGroup;
export const selectSearchResults = (state) =>
  state.categoryGroups.searchResults;
//export const selectImportResult = (state) => state.categoryGroups.importResult;
export const selectCategoryGroupsLoading = (state) =>
  state.categoryGroups.loading;
export const selectCategoryGroupsError = (state) => state.categoryGroups.error;

export default categoryGroupsSlice.reducer;
